import httpClient from '@/httpClient';
import router from '@/router';
import { getStorage, saveStorage } from '@/storageSave';
function getBffErrorMessage(body) {
    if (!body || typeof body !== 'object') {
        return 'There was an error saving the form';
    }
    if ('message' in body && typeof body.message === 'string') {
        return body.message;
    }
    if ('error' in body && typeof body.error === 'string') {
        return body.error;
    }
    return 'There was an error saving the form';
}
/**
 * Leaver Form Vuex Module
 *
 * This module is NAMESPACED (namespaced: true).
 * When accessing getters or dispatching actions from components, you MUST use the namespace prefix:
 *
 * - Getters: `store.getters['leaverForm/getLeaverFormData']` or use `mapGetters('leaverForm', ['getLeaverFormData'])`
 * - Actions: `store.dispatch('leaverForm/saveLeaverFormStep', ...)` or use `mapActions('leaverForm', ['saveLeaverFormStep'])`
 *
 * Root-style access (e.g., `store.getters.getLeaverFormData`) will return undefined and cause runtime errors.
 */
const leaverFormModule = {
    namespaced: true,
    state() {
        return {
            leaverForm: {
                leaverId: null,
                formOne: {
                    name: '',
                    email: '',
                    phone: '',
                    secondary: '',
                    secondaryName: '',
                    secondaryEmail: '',
                    secondaryPhone: '',
                    saved: false,
                },
                formTwo: {
                    is_temp: '',
                    employee_name: '',
                    unable_to_find: '',
                    employee_number: '',
                    division: '',
                    sector: '',
                    company_code: '',
                    personal_email: '',
                    location: '',
                    location_postcode: '',
                    saved: false,
                },
                formThree: {
                    reason: '',
                    reasonResignation: '',
                    comments: '',
                    saved: false,
                },
                formFour: {
                    re_employ: '',
                    saved: false,
                },
                formFive: {
                    files: [],
                    comments: '',
                    saved: false,
                },
                formSix: {
                    leaving_date: '',
                },
                formSeven: {
                    employee_reporting_to: '',
                    additional_information: '',
                    line_manager: '',
                    employee_number: '',
                    coupa_user: '',
                    coupa_user_name: '',
                    coupa_user_email: '',
                    coupa_user_number: '',
                },
                formEight: {
                    leave_situation: '',
                    leave_comments: '',
                    be_paid: '',
                    days_hours: '',
                },
                formNine: {
                    additional_leave: '',
                    current_leave: '',
                    comments_leave: '',
                    in_lieu: '',
                    redundancy_pay: '',
                    redundancy_pay_ni: '',
                    settlement_pay: '',
                    settlement_pay_ni: '',
                    ex_gratia: '',
                    ex_gratia_ni: '',
                    consideration_payment: '',
                    pension_fund: '',
                    deduction_payment: '',
                    reason_deduction: '',
                    value_deduction: '',
                    date_of_payment: '',
                },
                formTen: {},
            },
        };
    },
    getters: {
        getLeaver: (state) => state.leaverForm,
        getLeaverFormData: (state) => (formName) => state.leaverForm[formName],
        getLeaverId: (state) => state.leaverForm.leaverId,
        leaverFormIsReadyToSubmit: (state) => {
            const forms = ['formOne', 'formTwo', 'formThree', 'formFour', 'formFive'];
            let ready = true;
            Object.keys(state.leaverForm).forEach((form) => {
                if (forms.includes(form)) {
                    const formData = state.leaverForm[form];
                    if (formData && typeof formData === 'object' && 'saved' in formData) {
                        if (!formData.saved) {
                            ready = false;
                        }
                    }
                }
            });
            return ready;
        },
    },
    mutations: {
        setLeaverFormSavedStatus: (state, { formName, value }) => {
            const form = state.leaverForm[formName];
            if (form && typeof form === 'object' && 'saved' in form) {
                form.saved = value;
            }
        },
        setFormState: (state, { formName, formData }) => {
            state.leaverForm[formName] = { ...formData, saved: true };
        },
        setWholeFormState: (state, formData) => {
            state.leaverForm = formData;
        },
        setLeaverId: (state, id) => {
            state.leaverForm.leaverId = id;
        },
        updateFiles: (state, files) => {
            state.leaverForm.formFive.files = files;
        },
    },
    actions: {
        // Action to save a part of the leaver form
        saveLeaverFormStep: async ({ commit, state }, { event, requestedBy, id, stage, changes, edited, }) => {
            if (event.formData == null) {
                commit('setNotificationAll', {
                    visible: true,
                    type: 'error',
                    title: 'Error!',
                    message: 'Form data for this step is missing. Please refresh and try again.',
                }, { root: true });
                return false;
            }
            try {
                event.formData.saved = true;
                const payload = {
                    formData: event.formData,
                    requestedBy,
                    id,
                    stage,
                };
                if (edited) {
                    payload.edited = edited;
                    payload.changes = changes;
                }
                const response = await httpClient.post(`/bff/leaver/${event.name}`, payload);
                // Non-proxy BFF integrations return HTTP 200 with an embedded statusCode
                if (typeof response.data?.statusCode === 'number' &&
                    response.data.statusCode >= 400) {
                    event.formData.saved = false;
                    commit('setLeaverFormSavedStatus', {
                        formName: event.name,
                        value: false,
                    });
                    commit('setNotificationAll', {
                        visible: true,
                        type: 'error',
                        title: 'Error!',
                        message: getBffErrorMessage(response.data?.body),
                    }, { root: true });
                    return false;
                }
                const body = response.data?.body;
                if (!body || !body.id) {
                    throw new Error('Invalid response: missing id in response body');
                }
                commit('setNotificationAll', {
                    visible: true,
                    type: 'success',
                    title: 'Success!',
                    message: 'Form saved',
                }, { root: true });
                commit('setLeaverId', body.id);
                commit('setLeaverFormSavedStatus', {
                    formName: event.name,
                    value: true,
                });
                if (body.Item) {
                    commit('setWholeFormState', { ...body.Item, ...state.leaverForm });
                }
                saveStorage('leaverForm', JSON.stringify(state.leaverForm));
                if (event.name === 'formOne')
                    router.push(`/create-leaver/${body.id}`);
                return true;
            }
            catch (err) {
                event.formData.saved = false;
                commit('setLeaverFormSavedStatus', {
                    formName: event.name,
                    value: false,
                });
                const errBody = err &&
                    typeof err === 'object' &&
                    'response' in err &&
                    err.response &&
                    typeof err.response === 'object' &&
                    'data' in err.response &&
                    err.response.data &&
                    typeof err.response.data === 'object' &&
                    'body' in err.response.data
                    ? err.response.data.body
                    : undefined;
                const message = errBody?.message ??
                    errBody?.error ??
                    'There was an error saving the form';
                commit('setNotificationAll', {
                    visible: true,
                    type: 'error',
                    title: 'Error!',
                    message,
                }, { root: true });
                return false;
            }
        },
        // update file array in formFive
        updateFiles: ({ commit }, files) => {
            commit('updateFiles', files);
        },
        // Action to complete the leaver form
        completeLeaver: async ({ commit, state, rootGetters, }) => {
            try {
                // Get requestedBy from form data if available, otherwise from profile
                // The form data may have requestedBy if it was fetched from backend (setWholeFormState merges body.Item)
                // Use type assertion since LeaverForm interface doesn't include requestedBy but it may be present at runtime
                const formDataWithRequestedBy = state.leaverForm;
                const profile = rootGetters.getProfile;
                const requestedBy = formDataWithRequestedBy.requestedBy || profile?.userID;
                if (!requestedBy) {
                    commit('setNotificationAll', {
                        visible: true,
                        type: 'error',
                        title: 'Error!',
                        message: 'Unable to determine user. Please refresh and try again.',
                    }, { root: true });
                    return;
                }
                await httpClient.post('/bff/leaver/complete', {
                    id: state.leaverForm.leaverId,
                    requestedBy,
                });
                commit('setNotificationAll', {
                    visible: true,
                    type: 'success',
                    title: 'Success!',
                    message: 'The leaver form has been submitted',
                }, { root: true });
                router.push('/');
            }
            catch (_err) {
                commit('setNotificationAll', {
                    visible: true,
                    type: 'error',
                    title: 'Error!',
                    message: 'There was an error submitting the form',
                }, { root: true });
            }
        },
        // Action to update localstorage with the current leaver form state
        updateLeaverLocalStorage: ({ state, }) => {
            saveStorage('leaverForm', JSON.stringify(state.leaverForm));
        },
        updateLeaverStore: ({ commit, state, }) => {
            const storedData = getStorage('leaverForm');
            if (storedData) {
                const formData = JSON.parse(storedData);
                commit('setWholeFormState', { ...state.leaverForm, ...formData });
            }
        },
        setLeaverFormState: ({ commit }, { formName, formData }) => {
            commit('setFormState', { formName, formData });
        },
        clearAllLeaverFormData: ({ commit, }) => {
            commit('setWholeFormState', {
                leaverId: null,
                formOne: {
                    name: '',
                    email: '',
                    phone: '',
                    secondary: '',
                    secondaryName: '',
                    secondaryEmail: '',
                    secondaryPhone: '',
                    saved: false,
                },
                formTwo: {
                    is_temp: '',
                    employee_name: '',
                    unable_to_find: '',
                    employee_number: '',
                    division: '',
                    sector: '',
                    company_code: '',
                    personal_email: '',
                    location: '',
                    location_postcode: '',
                    saved: false,
                },
                formThree: {
                    reason: '',
                    reasonResignation: '',
                    comments: '',
                    saved: false,
                },
                formFour: {
                    re_employ: '',
                    saved: false,
                },
                formFive: {
                    files: [],
                    comments: '',
                    saved: false,
                },
                formSix: {
                    leaving_date: '',
                },
                formSeven: {
                    employee_reporting_to: '',
                    additional_information: '',
                    line_manager: '',
                    employee_number: '',
                    coupa_user: '',
                    coupa_user_name: '',
                    coupa_user_email: '',
                    coupa_user_number: '',
                },
                formEight: {
                    leave_situation: '',
                    leave_comments: '',
                    be_paid: '',
                    days_hours: '',
                },
                formNine: {
                    additional_leave: '',
                    current_leave: '',
                    comments_leave: '',
                    in_lieu: '',
                    redundancy_pay: '',
                    redundancy_pay_ni: '',
                    settlement_pay: '',
                    settlement_pay_ni: '',
                    ex_gratia: '',
                    ex_gratia_ni: '',
                    consideration_payment: '',
                    pension_fund: '',
                    deduction_payment: '',
                    reason_deduction: '',
                    value_deduction: '',
                    date_of_payment: '',
                },
                formTen: {},
            });
        },
    },
};
export default leaverFormModule;
